import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Loader2, Zap, Infinity as InfinityIcon } from "lucide-react";

/**
 * Wraps a Pro tool (Writer / Creator). Shows an upgrade/request-upgrade blocker
 * when the brand has no active subscription and no credits. Children only
 * render once the plan is active OR credits > 0.
 */
export default function UpgradeGate({ brand, role, children, feature }) {
  const [plan, setPlan] = useState(null); // {active, credits, is_owner}
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/brands/${brand.id}/plan`);
      setPlan(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [brand.id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center gap-2 text-[#858370]">
        <Loader2 className="w-4 h-4 animate-spin" /> Plan ophalen…
      </div>
    );
  }

  const hasAccess = plan && (plan.active || plan.credits > 0);
  if (hasAccess) {
    // Role check still applies: viewer can't actually run AI even with plan active.
    const canUseAI = role === "creator" || role === "manager" || role === "owner";
    if (!canUseAI) {
      return <RolePaywall feature={feature} />;
    }
    return (
      <>
        {typeof children === "function" ? children({ plan, refreshPlan: refresh }) : children}
        {plan && !plan.active && plan.credits > 0 && (
          <div className="fixed bottom-4 right-4 bg-white border border-[#858370]/30 rounded-full px-4 py-2 shadow-sm text-xs text-[#3a372b] flex items-center gap-2" data-testid="credits-pill">
            <Zap className="w-3 h-3" /> {plan.credits} credits over
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <UpgradeBlocker brand={brand} feature={feature} isOwner={plan?.is_owner} onOpen={() => setShowDialog(true)} />
      <UpgradeDialog brand={brand} open={showDialog} onOpenChange={setShowDialog} isOwner={plan?.is_owner} onRequested={refresh} />
    </>
  );
}

function RolePaywall({ feature }) {
  return (
    <div className="max-w-2xl mx-auto p-12">
      <div className="p-8 rounded-2xl bg-white border border-[#858370]/30">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-[#fef067] text-[#3a372b] mb-4">Alleen voor creators+</div>
        <h1 className="font-display text-4xl tracking-tight mb-3">{feature}</h1>
        <p className="text-[#858370] text-lg">Je rol is viewer. Vraag een manager of owner om jouw rol te upgraden naar creator.</p>
      </div>
    </div>
  );
}

function UpgradeBlocker({ brand, feature, isOwner, onOpen }) {
  return (
    <div className="max-w-2xl mx-auto p-12" data-testid="upgrade-blocker">
      <div className="p-8 rounded-2xl bg-white border border-[#858370]/30 bh-hard-shadow-sm">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-[#fef067] text-[#3a372b] mb-4">Pro</div>
        <h1 className="font-display text-4xl tracking-tight mb-3">{feature}</h1>
        <p className="text-[#858370] text-lg mb-6">
          {isOwner
            ? `Activeer een abonnement of koop credits om Writer en Creator voor ${brand.name} te gebruiken.`
            : `Writer en Creator zijn nog niet actief voor ${brand.name}. Vraag een upgrade aan — de owner krijgt een mail.`}
        </p>
        <Button data-testid="upgrade-cta" onClick={onOpen} className="rounded-full bh-btn-primary h-11 px-6">
          <Sparkles className="w-4 h-4 mr-2" /> {isOwner ? "Upgrade nu" : "Upgrade aanvragen"}
        </Button>
      </div>
    </div>
  );
}

function UpgradeDialog({ brand, open, onOpenChange, isOwner, onRequested }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const checkout = async (lookup_key) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/checkout`, {
        lookup_key,
        brand_id: brand.id,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout mislukt");
      setBusy(false);
    }
  };

  const requestUpgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/brands/${brand.id}/request-upgrade`, { note });
      if (data?.email_sent === false) {
        toast.warning("Verzoek opgeslagen, maar de mail kon niet verstuurd worden. Neem direct contact op met de owner.", { duration: 6000 });
      } else {
        toast.success("Upgrade-verzoek verstuurd naar de owner");
      }
      onRequested?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verzoek versturen mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upgrade-dialog" className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isOwner ? `Upgrade ${brand.name}` : `Vraag upgrade voor ${brand.name}`}
          </DialogTitle>
          <DialogDescription>
            {isOwner ? "Kies een plan. Testbetaling: kaart 4242 4242 4242 4242." : "De owner krijgt direct een mail met jouw verzoek."}
          </DialogDescription>
        </DialogHeader>

        {isOwner ? (
          <div className="grid gap-3">
            <button
              data-testid="plan-monthly"
              onClick={() => checkout("brand_pro_monthly")}
              disabled={busy}
              className="text-left p-5 rounded-xl border border-[#1a1a1a] bg-[#1a1a1a] text-white hover:opacity-95 disabled:opacity-60 transition"
            >
              <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-widest text-white/70"><InfinityIcon className="w-3.5 h-3.5" /> Onbeperkt</div>
              <div className="font-display text-2xl">€29 / maand</div>
              <div className="text-sm text-white/80 mt-1">Writer en Creator onbeperkt voor dit merk. Zeg maandelijks op.</div>
            </button>
            <button
              data-testid="plan-credits"
              onClick={() => checkout("brand_credits_50")}
              disabled={busy}
              className="text-left p-5 rounded-xl border border-[#858370]/30 bg-white hover:border-[#1a1a1a] disabled:opacity-60 transition"
            >
              <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-widest text-[#858370]"><Zap className="w-3.5 h-3.5" /> Credits</div>
              <div className="font-display text-2xl text-[#1a1a1a]">€15 — 50 credits</div>
              <div className="text-sm text-[#858370] mt-1">Eenmalig. 1 credit per Writer- of Creator-run.</div>
            </button>
            {busy && <div className="text-sm text-[#858370]">Een moment…</div>}
          </div>
        ) : (
          <div className="space-y-3">
            <label className="text-sm text-[#1a1a1a]">
              Optioneel bericht aan de owner
              <textarea
                data-testid="upgrade-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={400}
                className="mt-1 w-full min-h-[90px] border border-gray-200 rounded-lg px-3 py-2 text-sm"
                placeholder="Waarom je toegang wilt tot Writer / Creator…"
              />
            </label>
            <Button data-testid="request-upgrade-submit" onClick={requestUpgrade} disabled={busy} className="w-full h-11 rounded-full bh-btn-primary">
              {busy ? "Versturen…" : "Verstuur verzoek"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
